import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
	DEFAULT_EMAIL_DOMAIN_BLOCKED_MESSAGE,
	DEFAULT_BLOCKED_EMAIL_DOMAINS,
	fetchPublicAppConfig,
	normalizePublicAppConfig,
} from '../service/publicAppConfigService';

const PUBLIC_APP_CONFIG_QUERY_KEY = ['public-app-config'];

const DEFAULT_PUBLIC_APP_CONFIG = normalizePublicAppConfig({
	blockedEmailDomains: DEFAULT_BLOCKED_EMAIL_DOMAINS,
	emailDomainBlockedMessage: DEFAULT_EMAIL_DOMAIN_BLOCKED_MESSAGE,
	blockSubdomains: true,
});

const usePublicAppConfig = ({ enabled = true } = {}) => {
	const queryClient = useQueryClient();
	const {
		data: appConfig = DEFAULT_PUBLIC_APP_CONFIG,
		isFetching,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: PUBLIC_APP_CONFIG_QUERY_KEY,
		queryFn: fetchPublicAppConfig,
		enabled,
		staleTime: Infinity,
		gcTime: 30 * 60 * 1000,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		retry: 1,
	});

	const refresh = useCallback(
		async ({ force = false } = {}) => {
			try {
				if (force) {
					const result = await refetch();
					return result.data || DEFAULT_PUBLIC_APP_CONFIG;
				}

				return await queryClient.fetchQuery({
					queryKey: PUBLIC_APP_CONFIG_QUERY_KEY,
					queryFn: fetchPublicAppConfig,
					staleTime: Infinity,
				});
			} catch {
				return DEFAULT_PUBLIC_APP_CONFIG;
			}
		},
		[queryClient, refetch]
	);

	return {
		...appConfig,
		loading: enabled && (isLoading || isFetching),
		refresh,
	};
};

export default usePublicAppConfig;
